import React from 'react';

import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { IoArrowBackSharp } from 'react-icons/io5';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { req } from 'react-reqq-lite';
import { debounce, isEmpty, omit } from 'lodash';
import cn from 'classnames';
import { FaStoreAlt } from 'react-icons/fa';
import { useQueryParam } from 'use-query-params';

import logo from 'assets/images/logo.svg';
import { UserType } from 'modules/partial/components/Header';
import Notification from 'modules/partial/components/Notification';
import { Button, FormInputSelect } from 'modules/common/input';
import { AUTH_TYPE } from 'modules/partial/constants';
import { withAclReq } from 'modules/partial/hooks';
import { removeEmpty } from 'modules/common/utils';

import * as c from '../constants';
import { useGetBranchOpts } from '../hooks';

function Header({
  title,
  type,
  showDropdown,
  setShowDropdown,
  branch,
  onClose,
  titleClassName,
  isWaiterPage,
  withLogo,
  withGoBack,
  containerClassName,
  className,
  withDropdownShadow,
}) {
  const authType = React.useContext(AUTH_TYPE);

  const { branch_uuid, merchant_uuid } = useParams();
  const [isBranchOptsLoading, , includes] = useGetBranchOpts();
  const [orderUuid] = useQueryParam('order_uuid');
  const history = useHistory();

  const merchantMatch = useRouteMatch('/store-branches/:branch_uuid');
  const adminMatch = useRouteMatch(
    branch_uuid
      ? '/merchants/:merchant_uuid/store-branches/:branch_uuid'
      : '/merchants/:merchant_uuid'
  );

  const { values, setFieldValue } = useFormikContext() || {};

  const dispatchReturn = {
    ADMIN: `${adminMatch?.url}/services`,
    MERCHANT: branch_uuid ? `${merchantMatch?.url}/services` : '/services',
    BRANCH: '/services',
  };

  const onGoBackClick = () => {
    history.push('/services/waiter/all-orders');
    onClose();
  };

  const onGetBranchOptions = async (input = '', cb) => {
    const args = {
      keyword: input,
      sort_by: 'desc',
      per_page: 25,
    };

    await req.get(
      withAclReq(
        () => ({
          ADMIN: {
            url: `/v1/vendors/${merchant_uuid}/branches`,
          },
          MERCHANT: {
            url: '/v1/me/branches',
          },
        }),
        {
          key: c.BRANCH_OPTIONS,
          params: removeEmpty(args),
          onSuccess: (res) => {
            const { data: item } = res?.response;

            cb(
              item?.map((x) => ({
                ...omit(x, ['attributes', 'links', 'relationships']),
                ...(x?.attributes || {}),
                label: x?.attributes?.branch_name,
                value: x?.attributes?.uuid,
              }))
            );
          },
        }
      )
    );
  };

  const renderLogo = () => (
    <div
      className={cn('flex items-end gap-2 flex-1 mt-0.5 md:-mt-0.5', {
        'lg:px-[22px]': type === 'CUSTOMER',
        '!items-start sm:!items-end': type === 'BRANCH',
      })}
    >
      {withLogo && (
        <div>
          <img
            src={logo}
            alt=""
            className={cn('h-9 w-40 object-contain', {
              '!h-5 md:!h-9 !w-32 md:!w-40': type === 'BRANCH',
            })}
          />
        </div>
      )}

      <div
        className={cn('font-extrabold text-xl text-gray-800', titleClassName, {
          '!text-base md:!text-xl mt-px md:mt-0': type === 'BRANCH',
        })}
      >
        {title}
      </div>
    </div>
  );

  React.useEffect(() => {
    if (
      includes?.dateAsc?.length > 0 &&
      !isBranchOptsLoading &&
      type === 'MERCHANT' &&
      isEmpty(branch?.value)
    ) {
      const mainBranch = includes?.dateAsc?.[0];

      if (typeof setFieldValue !== 'function') return;

      setFieldValue('branch', {
        ...(mainBranch?.attributes || {}),
        id: mainBranch?.id,
        label: mainBranch?.attributes?.branch_name,
        value: mainBranch?.attributes?.uuid,
      });
    }
  }, [
    includes?.dateAsc,
    isBranchOptsLoading,
    setFieldValue,
    type,
    branch?.value,
  ]);

  return (
    <div
      className={cn(
        'flex flex-col p-3 lg:p-5 lg:px-10 lg:py-4 bg-white',
        containerClassName,
        {
          'drop-shadow-sm': withDropdownShadow,
        }
      )}
    >
      {type === 'BRANCH' && (
        <div className="sm:hidden mx-auto pb-1.5">{renderLogo()}</div>
      )}

      <div
        className={cn(
          'flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3',
          {
            'pt-1 !flex-row': type === 'BRANCH',
          }
        )}
      >
        <div className="flex gap-3 md:gap-4 items-center flex-1 -mt-1 lg:mt-0">
          {withGoBack && (
            <>
              {isWaiterPage ? (
                <div>
                  <Button
                    label={
                      <IoArrowBackSharp className="h-5 w-5 text-gray-700" />
                    }
                    className="btn sm bg-transparent hover:bg-gray-100 text-black border border-gray-300 !py-1.5"
                    to={dispatchReturn?.[authType]}
                  />
                </div>
              ) : (
                <>
                  {type !== 'CUSTOMER' && (
                    <div className="hidden md:block">
                      <Button
                        label={
                          <IoArrowBackSharp className="h-5 w-5 text-gray-700" />
                        }
                        className="btn sm bg-transparent hover:bg-gray-100 text-black border border-gray-300 !py-1.5"
                        to={dispatchReturn?.[authType]}
                      />
                    </div>
                  )}

                  <div className="md:hidden">
                    <Button
                      label={
                        <IoArrowBackSharp className="h-5 w-5 text-gray-700" />
                      }
                      className="btn sm bg-transparent hover:bg-gray-100 text-black border border-gray-300 !py-1.5"
                      onClick={orderUuid ? onGoBackClick : onClose}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div
            className={cn({
              'hidden sm:block': type === 'BRANCH',
            })}
          >
            {renderLogo()}
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center md:gap-2">
          {type === 'MERCHANT' && (
            <div className={cn('w-full lg:w-60 xl:w-96', className)}>
              <FormInputSelect
                label=""
                placeholder="Select a Branch"
                onSetFieldValue={setFieldValue}
                loadOptions={debounce(onGetBranchOptions, 1000)}
                value={values?.branch}
                name="branch"
                isLoading={isBranchOptsLoading}
                icon={<FaStoreAlt className="h-5 w-5 mr-1" />}
              />
            </div>
          )}

          <div>
            {type === 'BRANCH' && (
              <UserType
                data={{ ...branch, authType: 'Branch' }}
                withLoggedInLabel={authType === 'BRANCH'}
                // titleClassName="w-36 md:!w-auto"
              />
            )}
          </div>

          {type !== 'CUSTOMER' && (
            <div className="hidden">
              <Notification
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Header.defaultProps = {
  title: 'POS',
  type: 'BRANCH',
  branch: {},
  onClose: () => {},
  setShowDropdown: () => {},
  titleClassName: '',
  containerClassName: '',
  className: '',
  isWaiterPage: false,
  showDropdown: false,
  withDropdownShadow: true,
  withGoBack: true,
  withLogo: true,
};

Header.propTypes = {
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  className: PropTypes.string,
  isWaiterPage: PropTypes.bool,
  withDropdownShadow: PropTypes.bool,
  withGoBack: PropTypes.bool,
  withLogo: PropTypes.bool,
  type: PropTypes.string,
  showDropdown: PropTypes.bool,
  setShowDropdown: PropTypes.instanceOf(Function),
  branch: PropTypes.instanceOf(Object),
  onClose: PropTypes.instanceOf(Function),
};

export default Header;
